import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './StatutoryReport.css';

const StatutoryReport = () => {
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [payrolls, setPayrolls] = useState([]);
    const [payComponents, setPayComponents] = useState([]);
    const [employeesData, setEmployeesData] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [units, setUnits] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedPayroll, setSelectedPayroll] = useState('');
    const [reportType, setReportType] = useState('All');
    const [selectedEmployeeFields, setSelectedEmployeeFields] = useState([]);
    const [selectedPayComponents, setSelectedPayComponents] = useState([]);
    const [filters, setFilters] = useState({
        department_id: '',
        unit_id: '',
        designation_id: '',
        componentFilter: '' // New filter for components
    });
    const [showPreview, setShowPreview] = useState(false);
    const [reportSummary, setReportSummary] = useState(null);

    // Template states
    const [savedTemplates, setSavedTemplates] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [currentTemplateId, setCurrentTemplateId] = useState(null);
    const [saveAsDefault, setSaveAsDefault] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    // Alert states
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [showAlert, setShowAlert] = useState(false);

    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
    const previewRef = useRef(null);
    const alertTimeoutRef = useRef(null);
    const initialLoadRef = useRef(false);

    // Employee field options
    const employeeFieldOptions = [
        { id: 'emp_code', label: 'Employee Code', category: 'basic' },
        { id: 'name', label: 'Full Name', category: 'basic' },
        { id: 'first_name', label: 'First Name', category: 'basic' },
        { id: 'last_name', label: 'Last Name', category: 'basic' },
        { id: 'gender', label: 'Gender', category: 'personal' },
        { id: 'dob', label: 'Date of Birth', category: 'personal' },
        { id: 'doj', label: 'Date of Joining', category: 'employment' },
        { id: 'dor', label: 'Date of Resignation', category: 'employment' },
        { id: 'email', label: 'Email', category: 'contact' },
        { id: 'phone', label: 'Phone', category: 'contact' },
        { id: 'PF_NO', label: 'PF Number', category: 'statutory' },
        { id: 'UAN', label: 'UAN Number', category: 'statutory' },
        { id: 'ESI_NO', label: 'ESI Number', category: 'statutory' },
        { id: 'pan_number', label: 'PAN Number', category: 'statutory' },
        { id: 'aadhaar_number', label: 'Aadhaar Number', category: 'statutory' },
        { id: 'bank_name', label: 'Bank Name', category: 'bank' },
        { id: 'bank_account', label: 'Bank Account', category: 'bank' },
        { id: 'ifsc_code', label: 'IFSC Code', category: 'bank' },
        { id: 'dept_name', label: 'Department', category: 'organization' },
        { id: 'desig_name', label: 'Designation', category: 'organization' },
        { id: 'unit_name', label: 'Unit', category: 'organization' }
    ];

    // Report type configurations — wrapped in useMemo for stable reference
    const reportTypes = useMemo(() => ({
        'PF': {
            label: 'Provident Fund (PF)',
            icon: 'bi-shield-check',
            color: 'info',
            defaultEmployeeFields: ['emp_code', 'name', 'PF_NO', 'UAN', 'dept_name', 'desig_name'],
            defaultPayComponents: ['Basic', 'DA', 'PF', 'PF_Ceiling']
        },
        'ESI': {
            label: 'Employee State Insurance (ESI)',
            icon: 'bi-heart-pulse',
            color: 'danger',
            defaultEmployeeFields: ['emp_code', 'name', 'ESI_NO', 'dept_name', 'desig_name'],
            defaultPayComponents: ['Basic', 'DA', 'HRA', 'ESI', 'ESI_Ceiling']
        },
        'TDS': {
            label: 'Tax Deducted at Source (TDS)',
            icon: 'bi-calculator',
            color: 'warning',
            defaultEmployeeFields: ['emp_code', 'name', 'pan_number', 'dept_name'],
            defaultPayComponents: ['Basic', 'DA', 'HRA', 'Professional_Tax']
        },
        'Professional_Tax': {
            label: 'Professional Tax',
            icon: 'bi-building',
            color: 'secondary',
            defaultEmployeeFields: ['emp_code', 'name', 'dept_name', 'desig_name'],
            defaultPayComponents: ['Professional_Tax']
        },
        'All': {
            label: 'All Statutory Reports',
            icon: 'bi-file-earmark-text',
            color: 'primary',
            defaultEmployeeFields: ['emp_code', 'name', 'PF_NO', 'UAN', 'ESI_NO', 'pan_number', 'dept_name'],
            defaultPayComponents: ['Basic', 'DA', 'HRA', 'PF', 'ESI', 'Professional_Tax']
        }
    }), []);

    // Show alert function
    const showAlertMessage = useCallback((message, type = 'success', duration = 3000) => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);

        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }

        alertTimeoutRef.current = setTimeout(() => {
            setShowAlert(false);
        }, duration);
    }, []);

    const closeAlert = useCallback(() => {
        setShowAlert(false);
        if (alertTimeoutRef.current) {
            clearTimeout(alertTimeoutRef.current);
        }
    }, []);

    // Fetch payroll masters
    const fetchPayrolls = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}payroll/get_payrolls.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setPayrolls(response.data.payrolls || []);
            }
        } catch (error) {
            console.error('Error fetching payrolls:', error);
        }
    }, [API_BASE]);

    // Fetch pay components
    const fetchPayComponents = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}payroll/get_components.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setPayComponents(response.data.components || []);
            }
        } catch (error) {
            console.error('Error fetching pay components:', error);
        }
    }, [API_BASE]);

    // Fetch departments, units, designations
    const fetchFilters = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');

            // Fetch departments
            const deptResponse = await axios.get(`${API_BASE}/get_departments.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (deptResponse.data.success) {
                setDepartments(deptResponse.data.departments || []);
            }

            // Fetch units
            const unitResponse = await axios.get(`${API_BASE}/get_units.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (unitResponse.data.success) {
                setUnits(unitResponse.data.units || []);
            }

            // Fetch designations
            const desigResponse = await axios.get(`${API_BASE}/get_designations.php`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (desigResponse.data.success) {
                setDesignations(desigResponse.data.designations || []);
            }
        } catch (error) {
            console.error('Error fetching filters:', error);
        }
    }, [API_BASE]);

    // Fetch saved templates
    const fetchTemplates = useCallback(async (type = null) => {
        try {
            const token = localStorage.getItem('authToken');
            const params = type ? { report_type: type } : {};
            const response = await axios.get(`${API_BASE}statutory/save_template.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
            });
            if (response.data.success) {
                setSavedTemplates(response.data.templates || []);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    }, [API_BASE]);

    // Load template
    const loadTemplate = useCallback(async (templateId) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}statutory/save_template.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { template_id: templateId, action: 'get_template' }
            });

            if (response.data.success && response.data.template) {
                const template = response.data.template;

                setReportType(template.report_type);
                setSelectedEmployeeFields(template.employee_fields || []);
                setSelectedPayComponents(template.pay_components || []);
                setFilters(template.filters || { department_id: '', unit_id: '', designation_id: '', componentFilter: '' });
                setCurrentTemplateId(template.template_id);

                showAlertMessage('Template loaded successfully!');
            } else {
                showAlertMessage('Error loading template', 'error');
            }
        } catch (error) {
            console.error('Error loading template:', error);
            showAlertMessage('Error loading template: ' + (error.response?.data?.error || error.message), 'error');
        }
    }, [API_BASE, showAlertMessage]);

    // Load default template for report type
    const loadDefaultTemplate = useCallback(async (type) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE}statutory/save_template.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { action: 'get_default', report_type: type }
            });

            if (response.data.success && response.data.template) {
                const template = response.data.template;
                await loadTemplate(template.template_id);
            }
        } catch (error) {
            console.error('Error loading default template:', error);
        }
    }, [API_BASE, loadTemplate]);

    // Save template
    const saveTemplate = async (isUpdate = false) => {
        if (!templateName.trim()) {
            showAlertMessage('Please enter a template name', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const url = `${API_BASE}statutory/save_template.php`;

            const payload = {
                template_name: templateName,
                report_type: reportType,
                employee_fields: selectedEmployeeFields,
                pay_components: selectedPayComponents,
                filters: filters,
                output_format: 'pdf',
                is_default: saveAsDefault
            };

            let response;
            if (isUpdate && currentTemplateId) {
                payload.template_id = currentTemplateId;
                response = await axios.put(url, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } else {
                response = await axios.post(url, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            if (response.data.success) {
                showAlertMessage(isUpdate ? 'Template updated successfully!' : 'Template saved successfully!');
                setTemplateName('');
                setSaveAsDefault(false);
                setShowSaveDialog(false);
                if (!isUpdate) setCurrentTemplateId(response.data.template_id);
                fetchTemplates(reportType);
            } else {
                showAlertMessage('Error saving template: ' + response.data.error, 'error');
            }
        } catch (error) {
            console.error('Error saving template:', error);
            showAlertMessage('Error saving template: ' + (error.response?.data?.error || error.message), 'error');
        }
    };

    // Delete template
    const deleteTemplate = useCallback(async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.delete(`${API_BASE}statutory/save_template.php`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { template_id: templateId }
            });

            if (response.data.success) {
                showAlertMessage('Template deleted successfully!');
                fetchTemplates(reportType);
                if (currentTemplateId === templateId) {
                    setCurrentTemplateId(null);
                }
            } else {
                showAlertMessage('Error deleting template: ' + response.data.error, 'error');
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            showAlertMessage('Error deleting template: ' + (error.response?.data?.error || error.message), 'error');
        }
    }, [API_BASE, currentTemplateId, reportType, fetchTemplates, showAlertMessage]);

    // Generate preview
    const generatePreview = async () => {
        if (!selectedPayroll) {
            showAlertMessage('Please select a payroll first', 'error');
            return;
        }

        setPreviewLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}statutory/generate_report.php`,
                {
                    payroll_id: selectedPayroll,
                    report_type: reportType,
                    employee_fields: selectedEmployeeFields,
                    pay_components: selectedPayComponents,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                // Apply component-based filtering if specified
                let filteredData = response.data.employees || [];

                if (filters.componentFilter && filters.componentFilter !== 'all') {
                    if (filters.componentFilter === 'hasPF') {
                        filteredData = filteredData.filter(emp =>
                            (emp.PF_NO && emp.PF_NO.trim() !== '') ||
                            (emp.UAN && emp.UAN.trim() !== '')
                        );
                    } else if (filters.componentFilter === 'hasESI') {
                        filteredData = filteredData.filter(emp =>
                            emp.ESI_NO && emp.ESI_NO.trim() !== ''
                        );
                    } else if (filters.componentFilter === 'hasPAN') {
                        filteredData = filteredData.filter(emp =>
                            emp.pan_number && emp.pan_number.trim() !== ''
                        );
                    } else if (filters.componentFilter === 'hasComponentAmount') {
                        // Filter employees who have non-zero amounts for selected pay components
                        filteredData = filteredData.filter(emp => {
                            return selectedPayComponents.some(comp => {
                                const amount = parseFloat(emp[comp] || 0);
                                return amount > 0;
                            });
                        });
                    }
                }

                setEmployeesData(filteredData);
                setReportSummary(response.data.summary || {});
                setShowPreview(true);

                setTimeout(() => {
                    if (previewRef.current) {
                        previewRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);

                showAlertMessage(`Report generated: ${filteredData.length} employees found`, 'success');
            } else {
                showAlertMessage(`Error generating preview: ${response.data.error}`, 'error');
            }
        } catch (error) {
            console.error('Error generating preview:', error);
            showAlertMessage(`Error generating preview: ${error.message}`, 'error');
        } finally {
            setPreviewLoading(false);
        }
    };

    // Export to PDF
    const exportToPDF = async () => {
        if (!selectedPayroll) {
            showAlertMessage('Please select a payroll first', 'error');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}statutory/generate_report.php`,
                {
                    payroll_id: selectedPayroll,
                    report_type: reportType,
                    employee_fields: selectedEmployeeFields,
                    pay_components: selectedPayComponents,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, payroll_info, employees, report_type } = response.data;

                // Apply component-based filtering if specified
                let filteredData = employees || [];

                if (filters.componentFilter && filters.componentFilter !== 'all') {
                    if (filters.componentFilter === 'hasPF') {
                        filteredData = filteredData.filter(emp =>
                            (emp.PF_NO && emp.PF_NO.trim() !== '') ||
                            (emp.UAN && emp.UAN.trim() !== '')
                        );
                    } else if (filters.componentFilter === 'hasESI') {
                        filteredData = filteredData.filter(emp =>
                            emp.ESI_NO && emp.ESI_NO.trim() !== ''
                        );
                    } else if (filters.componentFilter === 'hasPAN') {
                        filteredData = filteredData.filter(emp =>
                            emp.pan_number && emp.pan_number.trim() !== ''
                        );
                    } else if (filters.componentFilter === 'hasComponentAmount') {
                        filteredData = filteredData.filter(emp => {
                            return selectedPayComponents.some(comp => {
                                const amount = parseFloat(emp[comp] || 0);
                                return amount > 0;
                            });
                        });
                    }
                }

                const doc = new jsPDF({
                    orientation: 'landscape',
                    unit: 'mm',
                    format: 'a4'
                });

                // Title
                doc.setFontSize(16);
                doc.setTextColor(40, 40, 40);
                const title = `${reportTypes[report_type].label} Report`;
                doc.text(title, 14, 20);

                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text(`Payroll Period: ${payroll_info.month_year}`, 14, 28);
                doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 34);

                // Company info
                if (company?.company_name) {
                    doc.text(`Company: ${company.company_name}`, 14, 42);
                }

                // Filter info
                let filterInfo = '';
                if (filters.department_id) {
                    const dept = departments.find(d => d.dept_id === filters.department_id);
                    if (dept) filterInfo += `Department: ${dept.dept_name} | `;
                }
                if (filters.unit_id) {
                    const unit = units.find(u => u.unit_id === filters.unit_id);
                    if (unit) filterInfo += `Unit: ${unit.unit_name} | `;
                }
                if (filters.componentFilter && filters.componentFilter !== 'all') {
                    const filterLabels = {
                        'hasPF': 'Employees with PF/UAN',
                        'hasESI': 'Employees with ESI',
                        'hasPAN': 'Employees with PAN',
                        'hasComponentAmount': 'Employees with non-zero component amounts'
                    };
                    filterInfo += `Filter: ${filterLabels[filters.componentFilter] || filters.componentFilter} | `;
                }
                if (filterInfo) {
                    doc.text(filterInfo.slice(0, -3), 14, 48);
                }

                // Prepare headers
                const headers = [
                    '#',
                    ...selectedEmployeeFields.map(field =>
                        employeeFieldOptions.find(f => f.id === field)?.label || field
                    ),
                    ...selectedPayComponents
                ];

                // Prepare data
                const tableData = filteredData.map((emp, index) => {
                    return [
                        (index + 1).toString(),
                        ...selectedEmployeeFields.map(field => {
                            if (field === 'name') {
                                return emp.name || '';
                            }
                            return emp[field] || '';
                        }),
                        ...selectedPayComponents.map(comp =>
                            parseFloat(emp[comp] || 0).toFixed(2)
                        )
                    ];
                });

                // Add totals row for amounts
                if (tableData.length > 0) {
                    const totals = [
                        '',
                        ...selectedEmployeeFields.map(() => ''),
                        'TOTALS',
                        ...selectedPayComponents.slice(1).map(comp =>
                            filteredData.reduce((sum, emp) => sum + parseFloat(emp[comp] || 0), 0).toFixed(2)
                        )
                    ];
                    tableData.push(totals);
                }

                // Add table
                doc.autoTable({
                    head: [headers],
                    body: tableData,
                    startY: 55,
                    styles: {
                        fontSize: 8,
                        cellPadding: 2
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold'
                    },
                    columnStyles: {
                        0: { cellWidth: 10 },
                        1: { cellWidth: 15 },
                        2: { cellWidth: 30 }
                    }
                });

                // Add summary
                const finalY = doc.lastAutoTable.finalY || 80;
                doc.setFontSize(9);
                doc.setTextColor(60, 60, 60);
                doc.text(`Total Employees: ${filteredData.length}`, 14, finalY + 10);

                // Save PDF
                const fileName = `${report_type.toLowerCase()}_report_${payroll_info.month_year.replace('/', '-')}.pdf`;
                doc.save(fileName);
                showAlertMessage('PDF exported successfully!');
            } else {
                showAlertMessage(`Error generating PDF: ${response.data.error}`, 'error');
            }
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            showAlertMessage(`Error generating PDF: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Export to Excel
    const exportToExcel = async () => {
        if (!selectedPayroll) {
            showAlertMessage('Please select a payroll first', 'error');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.post(`${API_BASE}statutory/generate_report.php`,
                {
                    payroll_id: selectedPayroll,
                    report_type: reportType,
                    employee_fields: selectedEmployeeFields,
                    pay_components: selectedPayComponents,
                    filters: filters,
                    format: 'json'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const { company, payroll_info, employees, report_type } = response.data;

                // Apply component-based filtering if specified
                let filteredData = employees || [];

                if (filters.componentFilter && filters.componentFilter !== 'all') {
                    if (filters.componentFilter === 'hasPF') {
                        filteredData = filteredData.filter(emp =>
                            (emp.PF_NO && emp.PF_NO.trim() !== '') ||
                            (emp.UAN && emp.UAN.trim() !== '')
                        );
                    } else if (filters.componentFilter === 'hasESI') {
                        filteredData = filteredData.filter(emp =>
                            emp.ESI_NO && emp.ESI_NO.trim() !== ''
                        );
                    } else if (filters.componentFilter === 'hasPAN') {
                        filteredData = filteredData.filter(emp =>
                            emp.pan_number && emp.pan_number.trim() !== ''
                        );
                    } else if (filters.componentFilter === 'hasComponentAmount') {
                        filteredData = filteredData.filter(emp => {
                            return selectedPayComponents.some(comp => {
                                const amount = parseFloat(emp[comp] || 0);
                                return amount > 0;
                            });
                        });
                    }
                }

                // Prepare data for Excel
                const excelData = [
                    [`${reportTypes[report_type].label} Report`],
                    [`Company: ${company.company_name}`],
                    [`Payroll Period: ${payroll_info.month_year}`],
                    [`Generated: ${new Date().toLocaleDateString()}`],
                ];

                // Add filter info
                let filterRow = ['Filters:'];
                if (filters.department_id) {
                    const dept = departments.find(d => d.dept_id === filters.department_id);
                    if (dept) filterRow.push(`Department: ${dept.dept_name}`);
                }
                if (filters.unit_id) {
                    const unit = units.find(u => u.unit_id === filters.unit_id);
                    if (unit) filterRow.push(`Unit: ${unit.unit_name}`);
                }
                if (filters.componentFilter && filters.componentFilter !== 'all') {
                    const filterLabels = {
                        'hasPF': 'Employees with PF/UAN',
                        'hasESI': 'Employees with ESI',
                        'hasPAN': 'Employees with PAN',
                        'hasComponentAmount': 'Employees with non-zero component amounts'
                    };
                    filterRow.push(`Component Filter: ${filterLabels[filters.componentFilter]}`);
                }
                excelData.push(filterRow);
                excelData.push(['']);

                // Add headers
                const headers = [
                    'SL No',
                    ...selectedEmployeeFields.map(field =>
                        employeeFieldOptions.find(f => f.id === field)?.label || field
                    ),
                    ...selectedPayComponents
                ];
                excelData.push(headers);

                // Add employee data
                filteredData.forEach((emp, index) => {
                    const row = [
                        index + 1,
                        ...selectedEmployeeFields.map(field => {
                            if (field === 'name') {
                                return emp.name || '';
                            }
                            return emp[field] || '';
                        }),
                        ...selectedPayComponents.map(comp =>
                            parseFloat(emp[comp] || 0).toFixed(2)
                        )
                    ];
                    excelData.push(row);
                });

                // Add totals row
                if (filteredData.length > 0) {
                    const totals = [
                        '',
                        ...selectedEmployeeFields.map(() => ''),
                        'TOTALS',
                        ...selectedPayComponents.slice(1).map(comp =>
                            filteredData.reduce((sum, emp) => sum + parseFloat(emp[comp] || 0), 0).toFixed(2)
                        )
                    ];
                    excelData.push(totals);
                }

                // Create workbook
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.aoa_to_sheet(excelData);

                // Style header row
                const range = XLSX.utils.decode_range(ws['!ref']);
                const headerRowIndex = 6; // Headers are in row 7
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    const address = XLSX.utils.encode_cell({ r: headerRowIndex, c: C });
                    if (!ws[address]) continue;
                    ws[address].s = {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: { fgColor: { rgb: "2980B9" } },
                        alignment: { horizontal: "center" }
                    };
                }

                XLSX.utils.book_append_sheet(wb, ws, 'Statutory Report');

                // Save file
                const fileName = `${report_type.toLowerCase()}_report_${payroll_info.month_year.replace('/', '-')}.xlsx`;
                XLSX.writeFile(wb, fileName);
                showAlertMessage('Excel exported successfully!');
            } else {
                showAlertMessage(`Error generating Excel: ${response.data.error}`, 'error');
            }
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            showAlertMessage(`Error generating Excel: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle report type change
    const handleReportTypeChange = useCallback((type) => {
        setReportType(type);
        setCurrentTemplateId(null);

        // Set default fields for the report type
        const config = reportTypes[type];
        if (config) {
            setSelectedEmployeeFields(config.defaultEmployeeFields);
            setSelectedPayComponents(config.defaultPayComponents);
        }

        // Reset filters
        setFilters({
            department_id: '',
            unit_id: '',
            designation_id: '',
            componentFilter: 'all'
        });

        // Fetch templates for this report type
        fetchTemplates(type);
    }, [fetchTemplates, reportTypes]);

    // Handle employee field selection
    const handleEmployeeFieldSelection = (fieldId) => {
        setSelectedEmployeeFields(prev =>
            prev.includes(fieldId)
                ? prev.filter(id => id !== fieldId)
                : [...prev, fieldId]
        );
    };

    // Handle pay component selection
    const handlePayComponentSelection = (componentName) => {
        setSelectedPayComponents(prev =>
            prev.includes(componentName)
                ? prev.filter(name => name !== componentName)
                : [...prev, componentName]
        );
    };

    // Handle select all employee fields in category
    const handleSelectAllEmployeeFields = (category) => {
        const categoryFields = employeeFieldOptions
            .filter(f => f.category === category)
            .map(f => f.id);

        const allSelected = categoryFields.every(field => selectedEmployeeFields.includes(field));

        if (allSelected) {
            setSelectedEmployeeFields(prev => prev.filter(field => !categoryFields.includes(field)));
        } else {
            setSelectedEmployeeFields(prev => {
                const newFields = [...prev];
                categoryFields.forEach(field => {
                    if (!newFields.includes(field)) {
                        newFields.push(field);
                    }
                });
                return newFields;
            });
        }
    };

    // Handle select all pay components
    const handleSelectAllPayComponents = () => {
        const allComponentNames = payComponents.map(comp => comp.component_name);

        if (selectedPayComponents.length === allComponentNames.length) {
            setSelectedPayComponents([]);
        } else {
            setSelectedPayComponents([...allComponentNames]);
        }
    };

    // Filter pay components based on search
    const getFilteredPayComponents = () => {
        if (filters.componentFilter === 'earning') {
            return payComponents.filter(comp => comp.component_type === 'Earning');
        } else if (filters.componentFilter === 'deduction') {
            return payComponents.filter(comp => comp.component_type === 'Deduction');
        } else if (filters.componentFilter === 'statutory') {
            return payComponents.filter(comp =>
                ['PF', 'ESI', 'Professional_Tax', 'TDS'].some(stat =>
                    comp.component_name.includes(stat)
                )
            );
        }
        return payComponents;
    };

    // Initialize component
    useEffect(() => {
        if (!initialLoadRef.current) {
            fetchPayrolls();
            fetchPayComponents();
            fetchFilters();
            handleReportTypeChange('PF'); // Default to PF report
            initialLoadRef.current = true;
        }

        return () => {
            if (alertTimeoutRef.current) {
                clearTimeout(alertTimeoutRef.current);
            }
        };
    }, [fetchPayrolls, fetchPayComponents, fetchFilters, handleReportTypeChange]);

    // When report type changes, try to load default template
    useEffect(() => {
        if (initialLoadRef.current) {
            loadDefaultTemplate(reportType);
        }
    }, [reportType, loadDefaultTemplate]);

    // Get selected payroll info
    const selectedPayrollInfo = payrolls.find(p => String(p.payroll_id) === String(selectedPayroll));

    // Get employee field categories
    const employeeFieldCategories = [
        { id: 'basic', label: 'Basic Information', icon: 'bi-person' },
        { id: 'personal', label: 'Personal Details', icon: 'bi-calendar' },
        { id: 'employment', label: 'Employment Details', icon: 'bi-briefcase' },
        { id: 'contact', label: 'Contact Information', icon: 'bi-telephone' },
        { id: 'statutory', label: 'Statutory Details', icon: 'bi-file-earmark-text' },
        { id: 'bank', label: 'Bank Details', icon: 'bi-bank' },
        { id: 'organization', label: 'Organization', icon: 'bi-building' }
    ];

    // Component filter options
    const componentFilterOptions = [
        { value: 'all', label: 'All Employees' },
        { value: 'hasPF', label: 'Employees with PF/UAN' },
        { value: 'hasESI', label: 'Employees with ESI' },
        { value: 'hasPAN', label: 'Employees with PAN' },
        { value: 'hasComponentAmount', label: 'Employees with Non-Zero Component Amounts' }
    ];

    return (
        <div className="container-fluid py-4 statutory-report-container">
            {/* Alert Notification */}
            {showAlert && (
                <div className={`alert alert-${alertType} alert-dismissible fade show mb-4`} role="alert">
                    <div className="d-flex align-items-center">
                        {alertType === 'success' ? (
                            <i className="bi bi-check-circle-fill me-2"></i>
                        ) : (
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        )}
                        <div>{alertMessage}</div>
                    </div>
                    <button type="button" className="btn-close" onClick={closeAlert}></button>
                </div>
            )}

            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <h2 className="h4 mb-1 fw-bold text-dark">
                                        <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                                        Statutory Reports
                                    </h2>
                                    <p className="text-muted mb-0">
                                        Generate PF, ESI, TDS, and other statutory compliance reports
                                    </p>
                                    {selectedPayrollInfo && (
                                        <div className="mt-2">
                                            <span className="badge bg-info me-2">
                                                <i className="bi bi-calendar me-1"></i>
                                                {selectedPayrollInfo.month_year}
                                            </span>
                                            <span className="badge bg-secondary">
                                                {selectedPayrollInfo.from_date} to {selectedPayrollInfo.to_date}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-6 text-end">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => window.history.back()}
                                    >
                                        <i className="bi bi-arrow-left me-2"></i>
                                        Back
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Configuration Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0">
                                <i className="bi bi-gear me-2 text-primary"></i>
                                Report Configuration
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">Report Type</label>
                                    <select
                                        className="form-select"
                                        value={reportType}
                                        onChange={(e) => handleReportTypeChange(e.target.value)}
                                    >
                                        {Object.entries(reportTypes).map(([key, config]) => (
                                            <option key={key} value={key}>
                                                {config.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Payroll Period</label>
                                    <select
                                        className="form-select"
                                        value={selectedPayroll}
                                        onChange={(e) => setSelectedPayroll(e.target.value)}
                                    >
                                        <option value="">Select a payroll</option>
                                        {payrolls.map(payroll => (
                                            <option key={payroll.payroll_id} value={payroll.payroll_id}>
                                                {payroll.month_year} - {payroll.from_date} to {payroll.to_date}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Component Filter</label>
                                    <select
                                        className="form-select"
                                        value={filters.componentFilter}
                                        onChange={(e) => setFilters(prev => ({ ...prev, componentFilter: e.target.value }))}
                                    >
                                        {componentFilterOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">&nbsp;</label>
                                    <button
                                        className="btn btn-primary w-100"
                                        onClick={generatePreview}
                                        disabled={!selectedPayroll || previewLoading}
                                    >
                                        {previewLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-eye me-2"></i>
                                                Generate Preview
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Template Management */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-save me-2 text-primary"></i>
                                    Report Templates
                                    <span className="badge bg-secondary ms-2">
                                        {reportTypes[reportType].label}
                                    </span>
                                </h5>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setShowSaveDialog(true)}
                                        disabled={selectedEmployeeFields.length === 0}
                                    >
                                        <i className="bi bi-save me-1"></i>
                                        Save Current as Template
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => fetchTemplates(reportType)}
                                    >
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            {savedTemplates.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bi bi-folder-x display-6"></i>
                                    <p className="mt-2">No templates saved for {reportTypes[reportType].label}</p>
                                    <p className="small">Save your current configuration to create a reusable template</p>
                                </div>
                            ) : (
                                <div className="row">
                                    {savedTemplates.map(template => (
                                        <div key={template.template_id} className="col-md-4 mb-3">
                                            <div className={`card h-100 ${currentTemplateId === template.template_id ? 'border-primary border-2' : 'border'}`}>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="card-title mb-0">
                                                            {template.template_name}
                                                            {template.is_default && (
                                                                <span className="badge bg-info ms-2">Default</span>
                                                            )}
                                                        </h6>
                                                        <div className="dropdown">
                                                            <button className="btn btn-sm btn-link" type="button" data-bs-toggle="dropdown">
                                                                <i className="bi bi-three-dots-vertical"></i>
                                                            </button>
                                                            <ul className="dropdown-menu">
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => loadTemplate(template.template_id)}
                                                                    >
                                                                        <i className="bi bi-box-arrow-in-down me-2"></i>
                                                                        Load
                                                                    </button>
                                                                </li>
                                                                <li><hr className="dropdown-divider" /></li>
                                                                <li>
                                                                    <button
                                                                        className="dropdown-item text-danger"
                                                                        onClick={() => deleteTemplate(template.template_id)}
                                                                    >
                                                                        <i className="bi bi-trash me-2"></i>
                                                                        Delete
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>

                                                    <div className="mb-2">
                                                        <span className={`badge bg-${reportTypes[template.report_type]?.color || 'secondary'} me-1`}>
                                                            {reportTypes[template.report_type]?.label || template.report_type}
                                                        </span>
                                                    </div>

                                                    <div className="mb-3">
                                                        <small className="text-muted">
                                                            <i className="bi bi-person me-1"></i>
                                                            {template.employee_fields?.length || 0} employee fields
                                                        </small>
                                                        <br />
                                                        <small className="text-muted">
                                                            <i className="bi bi-calculator me-1"></i>
                                                            {template.pay_components?.length || 0} pay components
                                                        </small>
                                                    </div>

                                                    <div className="mt-2 pt-2 border-top">
                                                        <small className="text-muted">
                                                            Updated: {new Date(template.updated_at).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                </div>
                                                {currentTemplateId === template.template_id && (
                                                    <div className="card-footer bg-primary bg-opacity-10 py-1">
                                                        <small className="text-primary">
                                                            <i className="bi bi-check-circle me-1"></i>
                                                            Currently loaded
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Save Template Modal */}
            {showSaveDialog && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="bi bi-save me-2"></i>
                                    Save Report Template
                                </h5>
                                <button type="button" className="btn-close" onClick={() => {
                                    setShowSaveDialog(false);
                                    setTemplateName('');
                                    setSaveAsDefault(false);
                                }}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Template Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={`e.g., ${reportTypes[reportType].label} Template`}
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        autoFocus
                                    />
                                </div>

                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="saveAsDefault"
                                            checked={saveAsDefault}
                                            onChange={(e) => setSaveAsDefault(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="saveAsDefault">
                                            Set as default template for {reportTypes[reportType].label}
                                        </label>
                                    </div>
                                </div>

                                <div className="alert alert-info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <div>
                                        <strong>Template Summary:</strong>
                                        <ul className="mb-0 mt-1">
                                            <li>{selectedEmployeeFields.length} employee fields selected</li>
                                            <li>{selectedPayComponents.length} pay components selected</li>
                                            <li>Report Type: {reportTypes[reportType].label}</li>
                                            {filters.componentFilter && filters.componentFilter !== 'all' && (
                                                <li>Component Filter: {componentFilterOptions.find(f => f.value === filters.componentFilter)?.label}</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowSaveDialog(false);
                                        setTemplateName('');
                                        setSaveAsDefault(false);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => saveTemplate(false)}
                                    disabled={!templateName.trim()}
                                >
                                    <i className="bi bi-save me-2"></i>
                                    Save Template
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Configuration Panels */}
            <div className="row">
                {/* Employee Fields Selection */}
                <div className="col-md-6 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0">
                                <i className="bi bi-person me-2 text-primary"></i>
                                Employee Information
                                <span className="badge bg-primary ms-2">
                                    {selectedEmployeeFields.length} Selected
                                </span>
                            </h5>
                            <small className="text-muted">
                                Select employee fields to include in the report
                            </small>
                        </div>
                        <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {employeeFieldCategories.map(category => {
                                const categoryFields = employeeFieldOptions.filter(f => f.category === category.id);
                                if (categoryFields.length === 0) return null;

                                const selectedCount = categoryFields.filter(f =>
                                    selectedEmployeeFields.includes(f.id)
                                ).length;

                                return (
                                    <div key={category.id} className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0">
                                                <i className={`bi ${category.icon} me-2`}></i>
                                                {category.label}
                                                <span className="badge bg-light text-dark ms-2">
                                                    {selectedCount}/{categoryFields.length}
                                                </span>
                                            </h6>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleSelectAllEmployeeFields(category.id)}
                                            >
                                                {selectedCount === categoryFields.length ? 'Deselect All' : 'Select All'}
                                            </button>
                                        </div>
                                        <div className="row">
                                            {categoryFields.map(field => (
                                                <div key={field.id} className="col-md-6 mb-2">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`field-${field.id}`}
                                                            checked={selectedEmployeeFields.includes(field.id)}
                                                            onChange={() => handleEmployeeFieldSelection(field.id)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`field-${field.id}`}>
                                                            {field.label}
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Pay Components Selection */}
                <div className="col-md-6 mb-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-0">
                                        <i className="bi bi-calculator me-2 text-primary"></i>
                                        Pay Components
                                        <span className="badge bg-info ms-2">
                                            {selectedPayComponents.length} Selected
                                        </span>
                                    </h5>
                                    <small className="text-muted">
                                        Filter:
                                        <select
                                            className="form-select form-select-sm d-inline-block ms-2"
                                            style={{ width: 'auto' }}
                                            value={filters.componentFilter}
                                            onChange={(e) => setFilters(prev => ({ ...prev, componentFilter: e.target.value }))}
                                        >
                                            <option value="all">All Components</option>
                                            <option value="earning">Earnings Only</option>
                                            <option value="deduction">Deductions Only</option>
                                            <option value="statutory">Statutory Only</option>
                                        </select>
                                    </small>
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleSelectAllPayComponents}
                                >
                                    {selectedPayComponents.length === getFilteredPayComponents().length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                        </div>
                        <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {getFilteredPayComponents().map(comp => (
                                <div key={comp.component_id} className="mb-2">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`comp-${comp.component_id}`}
                                            checked={selectedPayComponents.includes(comp.component_name)}
                                            onChange={() => handlePayComponentSelection(comp.component_name)}
                                        />
                                        <label className="form-check-label d-flex justify-content-between" htmlFor={`comp-${comp.component_id}`}>
                                            <span>
                                                {comp.component_name}
                                                <small className="text-muted d-block">
                                                    Type: {comp.component_type} |
                                                    Calculation: {comp.calculation_type}
                                                </small>
                                            </span>
                                            <span className={`badge bg-${comp.component_type === 'Earning' ? 'success' : comp.component_type === 'Deduction' ? 'danger' : 'secondary'}`}>
                                                {comp.component_type}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0">
                                <i className="bi bi-funnel me-2 text-primary"></i>
                                Advanced Filters
                            </h5>
                            <small className="text-muted">
                                Filter employees by department, unit, or designation
                            </small>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label">Department</label>
                                    <select
                                        className="form-select"
                                        value={filters.department_id}
                                        onChange={(e) => setFilters(prev => ({ ...prev, department_id: e.target.value }))}
                                    >
                                        <option value="">All Departments</option>
                                        {departments.map(dept => (
                                            <option key={dept.dept_id} value={dept.dept_id}>
                                                {dept.dept_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Unit</label>
                                    <select
                                        className="form-select"
                                        value={filters.unit_id}
                                        onChange={(e) => setFilters(prev => ({ ...prev, unit_id: e.target.value }))}
                                    >
                                        <option value="">All Units</option>
                                        {units.map(unit => (
                                            <option key={unit.unit_id} value={unit.unit_id}>
                                                {unit.unit_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Designation</label>
                                    <select
                                        className="form-select"
                                        value={filters.designation_id}
                                        onChange={(e) => setFilters(prev => ({ ...prev, designation_id: e.target.value }))}
                                    >
                                        <option value="">All Designations</option>
                                        {designations.map(desig => (
                                            <option key={desig.desig_id} value={desig.desig_id}>
                                                {desig.desig_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-2">
                                <button
                                    className="btn btn-danger"
                                    onClick={exportToPDF}
                                    disabled={loading || !selectedPayroll || selectedEmployeeFields.length === 0}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Generating PDF...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-file-pdf me-2"></i>
                                            Export PDF
                                        </>
                                    )}
                                </button>

                                <button
                                    className="btn btn-success"
                                    onClick={exportToExcel}
                                    disabled={loading || !selectedPayroll || selectedEmployeeFields.length === 0}
                                >
                                    <i className="bi bi-file-excel me-2"></i>
                                    Export Excel
                                </button>

                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setSelectedPayroll('');
                                        handleReportTypeChange(reportType); // Reset to defaults for current report type
                                        setShowPreview(false);
                                        setCurrentTemplateId(null);
                                        showAlertMessage('Configuration reset successfully!');
                                    }}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Section */}
            {showPreview && employeesData.length > 0 && (
                <div className="row" ref={previewRef}>
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className={`bi ${reportTypes[reportType].icon} me-2 text-${reportTypes[reportType].color}`}></i>
                                        {reportTypes[reportType].label} - Preview
                                        <span className="badge bg-primary ms-2">
                                            {employeesData.length} Employees
                                        </span>
                                    </h5>
                                    <div>
                                        <span className="badge bg-success me-2">
                                            {selectedEmployeeFields.length} Fields
                                        </span>
                                        <span className="badge bg-info">
                                            {selectedPayComponents.length} Components
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ maxHeight: '500px', overflow: 'auto' }}>
                                    <table className="table table-hover table-striped mb-0">
                                        <thead className="bg-light sticky-top">
                                            <tr>
                                                <th>#</th>
                                                {selectedEmployeeFields.map(field => (
                                                    <th key={field}>
                                                        {employeeFieldOptions.find(f => f.id === field)?.label || field}
                                                    </th>
                                                ))}
                                                {selectedPayComponents.map(comp => (
                                                    <th key={comp} className="text-end">{comp}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {employeesData.map((employee, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    {selectedEmployeeFields.map(field => (
                                                        <td key={field}>
                                                            {employee[field] || '-'}
                                                        </td>
                                                    ))}
                                                    {selectedPayComponents.map(comp => (
                                                        <td key={comp} className="text-end">
                                                            {parseFloat(employee[comp] || 0).toFixed(2)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Summary Stats */}
            {showPreview && reportSummary && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card bg-light border-0">
                            <div className="card-body py-3">
                                <div className="row text-center">
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="fw-bold text-primary fs-4">{reportSummary.total_employees}</div>
                                        <small className="text-muted">Total Employees</small>
                                    </div>
                                    {reportType === 'PF' || reportType === 'All' ? (
                                        <div className="col-md-3 mb-2 mb-md-0">
                                            <div className="fw-bold text-info fs-4">
                                                {reportSummary.total_pf_amount?.toFixed(2) || '0.00'}
                                            </div>
                                            <small className="text-muted">Total PF Amount</small>
                                        </div>
                                    ) : null}
                                    {reportType === 'ESI' || reportType === 'All' ? (
                                        <div className="col-md-3 mb-2 mb-md-0">
                                            <div className="fw-bold text-danger fs-4">
                                                {reportSummary.total_esi_amount?.toFixed(2) || '0.00'}
                                            </div>
                                            <small className="text-muted">Total ESI Amount</small>
                                        </div>
                                    ) : null}
                                    <div className="col-md-3 mb-2 mb-md-0">
                                        <div className="fw-bold text-success fs-4">
                                            {new Date().toLocaleDateString()}
                                        </div>
                                        <small className="text-muted">Generated Date</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatutoryReport;